import type { CurrencyCode } from "@stb/core/money"
import { isValidAbn, normaliseAbn } from "@stb/jurisdiction-au/abn"
import {
  lacksTaxInvoiceEvidence,
  TAX_INVOICE_THRESHOLD_MINOR,
} from "@stb/jurisdiction-au/tax-invoice"
import { z } from "zod"

import {
  EXTRACTION_REVIEW_CONFIDENCE_MIN,
  extractionStatusSchema,
  type ExtractionFields,
} from "./extractions.ts"
import { revisionSchema } from "./sync.ts"
import { holdReasonSchema, tripVisibilitySchema } from "./trips.ts"

/**
 * `expenses`: a confirmed or manually entered purchase (receipts plan 2.3). Amounts are integer
 * minor units plus `currency`. Request schemas are strict; the stored/response schema is lenient.
 */

export const expenseOriginSchema = z.enum(["ai", "manual"])
export type ExpenseOrigin = z.infer<typeof expenseOriginSchema>

export const expenseCategorySchema = z.enum([
  "tools_equipment",
  "vehicle",
  "phone_internet",
  "office_supplies",
  "insurance",
  "training",
  "subscriptions",
  "travel",
  "meals",
  "other",
])
export type ExpenseCategory = z.infer<typeof expenseCategorySchema>

/**
 * Currencies a new expense may use. Every value must be a core money `CurrencyCode` (the single
 * currency source, `@stb/core/money`); only AUD is accepted today. The column is plain text, so
 * stored rows stay lenient.
 */
export const EXPENSE_CURRENCIES = ["AUD"] as const satisfies readonly CurrencyCode[]
export const expenseCurrencySchema = z.enum(EXPENSE_CURRENCIES)

/** Server-owned flags. */
export const expenseFlagSchema = z.enum(["possible_duplicate"])
export type ExpenseFlag = z.infer<typeof expenseFlagSchema>

/** Largest accepted total: A$1,000,000,000.00 in cents. */
export const EXPENSE_MAX_TOTAL_MINOR = 100_000_000_000

/** `details.reason` of the CONFLICT when another device already confirmed the extraction. */
export const EXPENSE_EXTRACTION_ALREADY_CONFIRMED_REASON = "extraction_already_confirmed"

export const expenseSchema = z.object({
  id: z.uuid(),
  organisation_id: z.uuid(),
  document_id: z.uuid().nullable().default(null),
  extraction_id: z.uuid().nullable().default(null),
  origin: expenseOriginSchema,
  vendor_name: z.string(),
  vendor_abn: z.string().nullable().default(null),
  invoice_number: z.string().nullable().default(null),
  issued_on: z.string().date(),
  currency: z.string().default("AUD"),
  total_minor: z.number().int(),
  gst_minor: z.number().int().default(0),
  is_tax_invoice: z.boolean().default(false),
  category: expenseCategorySchema,
  business_pct: z.number().int().default(100),
  note: z.string().nullable().default(null),
  reviewed_at: z.string().datetime({ offset: true }).nullable().default(null),
  flags: z.array(z.string()).default([]),
  duplicate_of_id: z.uuid().nullable().default(null),
  visibility: tripVisibilitySchema.default("full"),
  hold_reason: holdReasonSchema.nullable().default(null),
  revision: revisionSchema,
  deleted_at: z.string().datetime({ offset: true }).nullable(),
  created_at: z.string().datetime({ offset: true }),
  updated_at: z.string().datetime({ offset: true }),
})

export type Expense = z.infer<typeof expenseSchema>

const abnRequestSchema = z
  .string()
  .transform(normaliseAbn)
  .refine(isValidAbn, "vendor_abn must be a valid 11 digit ABN")

const issuedOnRequestSchema = z
  .string()
  .date()
  .refine(
    (value) => Date.parse(`${value}T00:00:00Z`) <= Date.now() + 86_400_000,
    "issued_on must not be in the future",
  )

const totalMinorRequestSchema = z.number().int().min(1).max(EXPENSE_MAX_TOTAL_MINOR)

export const createExpenseRequestSchema = z
  .object({
    id: z.uuid().optional(),
    document_id: z.uuid().nullable().default(null),
    /** Filled by the client when it confirms an AI draft; unique per organisation. */
    extraction_id: z.uuid().nullable().default(null),
    origin: expenseOriginSchema,
    vendor_name: z.string().trim().min(1).max(120),
    vendor_abn: abnRequestSchema.nullable().default(null),
    invoice_number: z.string().trim().max(60).nullable().default(null),
    issued_on: issuedOnRequestSchema,
    currency: expenseCurrencySchema.default("AUD"),
    total_minor: totalMinorRequestSchema,
    gst_minor: z.number().int().min(0).default(0),
    is_tax_invoice: z.boolean().default(false),
    category: expenseCategorySchema,
    business_pct: z.number().int().min(0).max(100).default(100),
    note: z.string().trim().max(500).nullable().default(null),
    reviewed_at: z.string().datetime({ offset: true }).nullable().default(null),
  })
  .refine((data) => data.gst_minor <= data.total_minor, {
    message: "gst_minor must not exceed total_minor",
    path: ["gst_minor"],
  })

export type CreateExpenseRequest = z.input<typeof createExpenseRequestSchema>
export type ParsedCreateExpenseRequest = z.infer<typeof createExpenseRequestSchema>

/**
 * Update request. `total_minor` and `gst_minor` are a coupled pair: send both or neither, so
 * the field-level merge never leaves a GST larger than the total.
 */
export const updateExpenseRequestSchema = z
  .object({
    base_revision: z.number().int().nonnegative().optional(),
    vendor_name: z.string().trim().min(1).max(120).optional(),
    vendor_abn: abnRequestSchema.nullable().optional(),
    invoice_number: z.string().trim().max(60).nullable().optional(),
    issued_on: issuedOnRequestSchema.optional(),
    total_minor: totalMinorRequestSchema.optional(),
    gst_minor: z.number().int().min(0).optional(),
    is_tax_invoice: z.boolean().optional(),
    category: expenseCategorySchema.optional(),
    business_pct: z.number().int().min(0).max(100).optional(),
    note: z.string().trim().max(500).nullable().optional(),
    reviewed_at: z.string().datetime({ offset: true }).nullable().optional(),
  })
  .superRefine((data, ctx) => {
    if ((data.total_minor === undefined) !== (data.gst_minor === undefined)) {
      ctx.addIssue({
        code: "custom",
        message: "total_minor and gst_minor must be sent together",
        path: [data.total_minor === undefined ? "total_minor" : "gst_minor"],
      })
    } else if (
      data.total_minor !== undefined &&
      data.gst_minor !== undefined &&
      data.gst_minor > data.total_minor
    ) {
      ctx.addIssue({
        code: "custom",
        message: "gst_minor must not exceed total_minor",
        path: ["gst_minor"],
      })
    }
  })

export type UpdateExpenseRequest = z.infer<typeof updateExpenseRequestSchema>

// --- Derived values (TS reference; test vectors expenses.json / expense-review.json) ---------

export const expenseReviewReasonSchema = z.enum([
  "not_reviewed",
  "low_confidence",
  "providers_disagree",
  "gst_mismatch",
  "missing_tax_invoice",
  "possible_duplicate",
])
export type ExpenseReviewReason = z.infer<typeof expenseReviewReasonSchema>

/**
 * Extraction fields whose confidence is checked against the review threshold. `abn` is only
 * checked when a tax invoice is required (total above the A$82.50 threshold), see
 * `expenseReviewReasons`.
 */
export const REVIEW_CONFIDENCE_KEYS = [
  "vendor_name",
  "issued_on",
  "total_minor",
  "gst_minor",
  "abn",
] as const

export interface ExpenseReviewInput {
  reviewed_at: string | null
  total_minor: number
  gst_minor: number
  vendor_abn: string | null
  is_tax_invoice: boolean
  flags: readonly string[]
}

export interface ExtractionReviewInput {
  fields: ExtractionFields
  disagreement_fields: readonly string[]
}

/** Integer division of `total / 11` rounded half up (the GST contained in a GST-inclusive total). */
function expectedGstMinor(totalMinor: number): number {
  return Math.floor((2 * totalMinor + 11) / 22)
}

/**
 * Why an expense still needs review, in a fixed order; `[]` means out of the inbox.
 *
 * - Once `reviewed_at` is set the expense is confirmed and the result is always `[]` (R3.3,
 *   R4.13). Missing tax invoice evidence on a confirmed expense is only the detail-page warning
 *   in `gstCreditEstimate` (R3.4 requires a prompt, not a block).
 * - `low_confidence`: a key field below the threshold (`vendor_name`, `issued_on`,
 *   `total_minor`, `gst_minor`; `abn` only when `total_minor` is above the tax invoice
 *   threshold, because a small non-tax receipt needs no ABN).
 * - `gst_mismatch`: `gst_minor` deviates from `total / 11` by more than 2 cents and more than 1%
 *   of the expected GST. `gst_minor` 0 (GST-free) is not a mismatch when no tax is claimed
 *   (no extraction, i.e. a manual expense) or when the extraction itself read GST as 0; with an
 *   extraction that read a non-zero GST, or none at all, a 0 means the draft lost the tax.
 */
export function expenseReviewReasons(
  expense: ExpenseReviewInput,
  extraction?: ExtractionReviewInput | null,
): ExpenseReviewReason[] {
  if (expense.reviewed_at !== null) return []
  const reasons: ExpenseReviewReason[] = ["not_reviewed"]
  const taxInvoiceRequired = expense.total_minor > TAX_INVOICE_THRESHOLD_MINOR
  if (extraction) {
    const lowConfidence = REVIEW_CONFIDENCE_KEYS.some((key) => {
      if (key === "abn" && !taxInvoiceRequired) return false
      const confidence = extraction.fields[key]?.confidence
      return confidence !== undefined && confidence < EXTRACTION_REVIEW_CONFIDENCE_MIN
    })
    if (lowConfidence) reasons.push("low_confidence")
    if (extraction.disagreement_fields.length > 0) reasons.push("providers_disagree")
  }
  if (gstMismatch(expense, extraction)) reasons.push("gst_mismatch")
  if (
    lacksTaxInvoiceEvidence({
      totalMinor: expense.total_minor,
      hasAbn: expense.vendor_abn !== null,
      isTaxInvoice: expense.is_tax_invoice,
    })
  ) {
    reasons.push("missing_tax_invoice")
  }
  if (expense.flags.includes(expenseFlagSchema.enum.possible_duplicate)) {
    reasons.push("possible_duplicate")
  }
  return reasons
}

function gstMismatch(
  expense: ExpenseReviewInput,
  extraction: ExtractionReviewInput | null | undefined,
): boolean {
  if (expense.gst_minor === 0) {
    return extraction != null && extraction.fields.gst_minor?.value !== 0
  }
  const expected = expectedGstMinor(expense.total_minor)
  const deviation = Math.abs(expense.gst_minor - expected)
  return deviation > 2 && deviation * 100 > expected
}

export const documentListStateSchema = z.enum(["to_read", "reading", "draft", "done"])
export type DocumentListState = z.infer<typeof documentListStateSchema>

/**
 * Where a document sits in the receipts list: `done` once a live expense exists; otherwise
 * `draft` (extraction succeeded, awaiting confirmation), `reading` (queued or running) or
 * `to_read` (nothing usable yet, including failed and quota-skipped).
 */
export function documentListState(
  extraction: { status: string } | null | undefined,
  expense: unknown,
): DocumentListState {
  if (expense) return "done"
  if (extraction?.status === extractionStatusSchema.enum.succeeded) return "draft"
  if (
    extraction?.status === extractionStatusSchema.enum.queued ||
    extraction?.status === extractionStatusSchema.enum.running
  )
    return "reading"
  return "to_read"
}
